// The task drawer's checklists (DOCS-KB decision 8; design 2026-10-21 §2 "Task drawer ›
// Checklists"; ClickUp/Asana's checklists in Linear's density): several named checklists per
// task, each a section with its title edited inline, "3/5 done", a thin progress bar and a `…`
// menu (Rename, Hide completed, Delete checklist; Rename and Delete also hover icons). Rows are 32 px: a drag handle, the checkbox,
// the text (click to edit), the assignee, the due date and Delete, the last three on hover when
// empty. Drag reorders within a checklist (one `tasks.reorderChecklist` on drop) or moves an item
// to another; Alt+↑/↓ is the drag's keyboard twin (like `BoardView`). "Hide completed" is kept
// per checklist in this browser only.

import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Checkbox,
  cn,
  DrawerSection,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DueLabel,
  Field,
  FieldLabel,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Progress,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CalendarIcon,
  EllipsisIcon,
  GripVerticalIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import {
  type DragEvent,
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import type { CompanyAccess } from "../company";
import { AssigneeMenu } from "./inline-editors";
import { dueInstant, localDay, type SeatPeople } from "./model";
import { queued, taskKeys, updateTask } from "./task-cache";
import { Collapsible } from "./task-sections";
import type { TaskDetail } from "./view-engine";
import { workKeys } from "./work-queries";

type Item = TaskDetail["checklist_items"][number];
type List = TaskDetail["checklists"][number];
type ItemPatch = {
  text?: string;
  done?: boolean;
  checklist_id?: string;
  assignee_seat_id?: string | null;
  due_at?: string | null;
};

/** Shown on hover or focus of the row, while its menu is open, and always on touch. */
const ON_HOVER =
  "opacity-0 group-hover/item:opacity-100 group-focus-within/item:opacity-100 focus-visible:opacity-100 data-popup-open:opacity-100 pointer-coarse:opacity-100";
/** A checklist's own Rename / Delete icons: on hover or focus of its section. */
const CHECKLIST_HOVER =
  "opacity-0 group-hover/checklist:opacity-100 group-focus-within/checklist:opacity-100 pointer-coarse:opacity-100";

const byPos = <T extends { position: number; id: string }>(a: T, b: T) =>
  a.position - b.position || a.id.localeCompare(b.id);

const hideKey = (id: string) => `companyos.work.checklist.hide.${id}`;

function readHidden(id: string): boolean {
  try {
    return localStorage.getItem(hideKey(id)) === "1";
  } catch {
    return false;
  }
}

function writeHidden(id: string, on: boolean) {
  try {
    if (on) localStorage.setItem(hideKey(id), "1");
    else localStorage.removeItem(hideKey(id));
  } catch {
    // Private mode or blocked storage: the toggle lasts for this visit.
  }
}

/** `ids` with `id` moved to just before `anchor` (to the end when `anchor` is null). */
export function placeBefore(ids: readonly string[], id: string, anchor: string | null): string[] {
  const rest = ids.filter((x) => x !== id);
  const at = anchor === null ? -1 : rest.indexOf(anchor);
  return at < 0 ? [...rest, id] : [...rest.slice(0, at), id, ...rest.slice(at)];
}

const sameOrder = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Every checklist write (WORK-PERF): the drawer and the card's "2/5" counter change at once,
 * the server's copy replaces the guess, and a refusal puts both back (with the error toast).
 * Writes of one task go out in order (task-cache.ts `queued`), so a tick right after an add
 * waits for the item to exist. Lists (create, rename, delete) re-read the task after.
 */
function useChecklistWrites(api: Api, co: string, taskId: string) {
  const queryClient = useQueryClient();
  const key = workKeys.task(co, taskId);
  const keys = () => taskKeys(co);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const p = { co, id: taskId };
  /** Patches the detail, then the card's counts everywhere the task shows. */
  const patchCache = (fn: (t: TaskDetail) => TaskDetail) => {
    const next = queryClient.setQueryData<TaskDetail>(key, (old) => (old ? fn(old) : old));
    if (!next) return;
    const checklist = {
      done: next.checklist_items.filter((i) => i.done_at !== null).length,
      total: next.checklist_items.length,
    };
    updateTask(queryClient, co, taskId, (t) => ({ ...t, checklist }));
  };
  const item = (i: Item) =>
    patchCache((t) => ({
      ...t,
      checklist_items: t.checklist_items.map((x) => (x.id === i.id ? i : x)),
    }));
  /** The server's id for an item added optimistically (its local id until then). */
  const real = useRef(new Map<string, Promise<string>>()).current;
  const idOf = (id: string) => real.get(id) ?? Promise.resolve(id);
  const createList = useMutation({
    mutationFn: (title: string) => api.client.tasks.createChecklist({ params: p, body: { title } }),
    onSettled: refresh,
  });
  const renameList = useOptimisticMutation({
    keys,
    mutationFn: (v: { id: string; title: string }) =>
      queued(taskId, () =>
        api.client.tasks.updateChecklist({
          params: { ...p, checklist_id: v.id },
          body: { title: v.title },
        }),
      ),
    apply: (v) =>
      patchCache((t) => ({
        ...t,
        checklists: t.checklists.map((l) => (l.id === v.id ? { ...l, title: v.title } : l)),
      })),
  });
  const removeList = useMutation({
    mutationFn: (id: string) =>
      api.client.tasks.deleteChecklist({ params: { ...p, checklist_id: id } }),
    onSettled: refresh,
  });
  const add = useOptimisticMutation({
    keys,
    apply: (v: { listId: string; text: string; localId: string }) =>
      patchCache((t) => {
        const now = new Date().toISOString();
        const last = t.checklist_items.filter((i) => i.checklist_id === v.listId).at(-1);
        return {
          ...t,
          checklist_items: [
            ...t.checklist_items,
            {
              company_id: co,
              id: v.localId,
              task_id: taskId,
              checklist_id: v.listId,
              text: v.text,
              done_at: null,
              done_by_seat_id: null,
              assignee_seat_id: null,
              due_at: null,
              position: (last?.position ?? -1) + 1,
              actor_kind: null,
              actor_membership_id: null,
              created_at: now,
              updated_at: now,
            },
          ],
        };
      }),
    mutationFn: (v) => {
      const sent = queued(taskId, () =>
        api.client.tasks.addChecklistItem({
          params: p,
          body: { text: v.text, checklist_id: v.listId },
        }),
      );
      real.set(
        v.localId,
        sent.then((i) => i.id),
      );
      return sent;
    },
    reconcile: (i, v) =>
      patchCache((t) => {
        // Edits made to the local copy meanwhile (a tick) stay; the id and stamps are the server's.
        const local = t.checklist_items.find((x) => x.id === v.localId);
        return {
          ...t,
          checklist_items: t.checklist_items.map((x) =>
            x.id === v.localId
              ? { ...i, text: local?.text ?? i.text, done_at: local?.done_at ?? i.done_at }
              : x,
          ),
        };
      }),
  });
  const update = useOptimisticMutation({
    keys,
    apply: ({ id, patch }: { id: string; patch: ItemPatch }) =>
      patchCache((t) => ({
        ...t,
        checklist_items: t.checklist_items.map((i) => {
          if (i.id !== id) return i;
          const { done, ...rest } = patch;
          return {
            ...i,
            ...rest,
            ...(done === undefined
              ? {}
              : { done_at: done ? (i.done_at ?? new Date().toISOString()) : null }),
            // A moved item goes last in its new checklist.
            ...(patch.checklist_id && patch.checklist_id !== i.checklist_id
              ? { position: Number.MAX_SAFE_INTEGER }
              : {}),
          };
        }),
      })),
    mutationFn: (v) =>
      queued(taskId, async () =>
        api.client.tasks.updateChecklistItem({
          params: { ...p, item_id: await idOf(v.id) },
          body: v.patch,
        }),
      ),
    reconcile: (i, v) => {
      // A move is placed by the server (its position); otherwise the shown copy is already right.
      if (v.patch.checklist_id) item(i);
    },
  });
  const remove = useOptimisticMutation({
    keys,
    apply: (id: string) =>
      patchCache((t) => ({
        ...t,
        checklist_items: t.checklist_items.filter((i) => i.id !== id),
      })),
    mutationFn: (id) =>
      queued(taskId, async () =>
        api.client.tasks.deleteChecklistItem({ params: { ...p, item_id: await idOf(id) } }),
      ),
  });
  const reorder = useOptimisticMutation({
    keys,
    apply: (v: { listId: string; ids: string[] }) =>
      patchCache((t) => ({
        ...t,
        checklist_items: t.checklist_items.map((i) => {
          const at = v.ids.indexOf(i.id);
          return at < 0 ? i : { ...i, position: at };
        }),
      })),
    mutationFn: (v) =>
      queued(taskId, async () =>
        api.client.tasks.reorderChecklist({
          params: { ...p, checklist_id: v.listId },
          body: { item_ids: await Promise.all(v.ids.map(idOf)) },
        }),
      ),
  });
  return {
    createList,
    renameList: (id: string, title: string) => renameList.mutate({ id, title }),
    removeList: (id: string) => removeList.mutate(id),
    add: (listId: string, text: string, onError: () => void) =>
      add.mutate({ listId, text, localId: newIdempotencyKey() }, { onError }),
    update: (id: string, patch: ItemPatch) => update.mutate({ id, patch }),
    remove: (id: string) => remove.mutate(id),
    reorder: (listId: string, ids: string[]) => reorder.mutate({ listId, ids }),
    /** To another checklist (last there), then into place when an anchor is given. */
    move: async (id: string, toList: string, anchor: string | null, toIds: string[]) => {
      await update.mutateAsync({ id, patch: { checklist_id: toList } });
      if (anchor !== null) reorder.mutate({ listId: toList, ids: placeBefore(toIds, id, anchor) });
    },
  };
}

type Writes = ReturnType<typeof useChecklistWrites>;
type Drag = { id: string; from: string };
type Target = { list: string; index: number };

export function ChecklistsSection({
  api,
  company,
  task,
  people,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  people: SeatPeople;
}) {
  const w = useChecklistWrites(api, company.id, task.id);
  // The checklist just made: its "Add an item" field takes the focus.
  const [fresh, setFresh] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const lists = [...task.checklists].sort(byPos);
  const itemsOf = (id: string) =>
    task.checklist_items.filter((i) => i.checklist_id === id).sort(byPos);
  const addList = () =>
    w.createList.mutate("Checklist", { onSuccess: (l: { id: string }) => setFresh(l.id) });

  function onDrop(list: string, visible: Item[]) {
    const d = drag;
    const at = target?.list === list ? target.index : visible.length;
    setDrag(null);
    setTarget(null);
    if (!d) return;
    const anchor = visible[at]?.id ?? null;
    if (anchor === d.id) return;
    const ids = itemsOf(list).map((i) => i.id);
    if (d.from === list) {
      const next = placeBefore(ids, d.id, anchor);
      if (!sameOrder(next, ids)) w.reorder(list, next);
    } else void w.move(d.id, list, anchor, ids);
  }

  return (
    <Collapsible name="Checklist" add="Add checklist" hasContent={lists.length > 0} onAdd={addList}>
      {lists.map((l) => (
        <ChecklistBlock
          key={l.id}
          list={l}
          items={itemsOf(l.id)}
          people={people}
          writes={w}
          autoFocusAdd={fresh === l.id}
          drag={drag}
          target={target}
          onDragStart={(id) => setDrag({ id, from: l.id })}
          onDragEnd={() => {
            setDrag(null);
            setTarget(null);
          }}
          onTarget={setTarget}
          onDrop={(visible) => onDrop(l.id, visible)}
        />
      ))}
      {lists.length ? (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 mt-2 h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
          disabled={w.createList.isPending}
          onClick={addList}
        >
          <PlusIcon aria-hidden="true" />
          Add checklist
        </Button>
      ) : null}
    </Collapsible>
  );
}

function ChecklistBlock({
  list,
  items,
  people,
  writes: w,
  autoFocusAdd,
  drag,
  target,
  onDragStart,
  onDragEnd,
  onTarget,
  onDrop,
}: {
  list: List;
  items: Item[];
  people: SeatPeople;
  writes: Writes;
  autoFocusAdd: boolean;
  drag: Drag | null;
  target: Target | null;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onTarget: (t: Target | null) => void;
  onDrop: (visible: Item[]) => void;
}) {
  const [hidden, setHidden] = useState(() => readHidden(list.id));
  const [renaming, setRenaming] = useState(false);
  const [text, setText] = useState("");
  const titleInput = useRef<HTMLInputElement>(null);
  const refocus = useRef<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const done = items.filter((i) => i.done_at).length;
  const visible = hidden ? items.filter((i) => !i.done_at) : items;
  const today = localDay(new Date());

  // After a keyboard move the row re-renders in its new place: keep the focus on its text.
  useEffect(() => {
    if (!refocus.current) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-checklist-item="${CSS.escape(refocus.current)}"] [data-item-text]`,
    );
    if (el) {
      el.focus();
      refocus.current = null;
    }
  });

  function onRowKeyDown(e: KeyboardEvent<HTMLLIElement>, id: string) {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    if ((e.target as HTMLElement).tagName === "INPUT") return;
    const at = visible.findIndex((i) => i.id === id);
    const anchor =
      e.key === "ArrowUp"
        ? at > 0
          ? visible[at - 1]?.id
          : undefined
        : at < visible.length - 1
          ? (visible[at + 2]?.id ?? null)
          : undefined;
    if (anchor === undefined) return;
    e.preventDefault();
    const ids = items.map((i) => i.id);
    const next = placeBefore(ids, id, anchor);
    if (sameOrder(next, ids)) return;
    refocus.current = id;
    w.reorder(list.id, next);
  }

  function onDragOver(e: DragEvent<HTMLUListElement>) {
    if (!drag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rows = Array.from(e.currentTarget.querySelectorAll<HTMLElement>("[data-checklist-item]"));
    let index = rows.length;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i]?.getBoundingClientRect();
      if (r && e.clientY < r.top + r.height / 2) {
        index = i;
        break;
      }
    }
    if (target?.list !== list.id || target.index !== index) onTarget({ list: list.id, index });
  }

  const addItem = () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    w.add(list.id, t, () => setText(t));
  };

  const title = (
    <span className="flex min-w-0 items-center gap-2">
      <InlineText
        value={list.title}
        editing={renaming}
        onEditingChange={setRenaming}
        inputRef={titleInput}
        inputLabel="Checklist title"
        maxLength={200}
        buttonClassName="min-w-0 truncate rounded-sm text-left hover:bg-muted"
        inputClassName="h-7 px-2 text-[15px] font-semibold"
        onSave={(v) => w.renameList(list.id, v)}
      />
      {items.length ? (
        <span className="shrink-0 text-xs font-normal text-muted-foreground tabular-nums">
          {done}/{items.length} done
        </span>
      ) : null}
    </span>
  );

  return (
    <DrawerSection
      data-checklist={list.id}
      title={title}
      className="group/checklist"
      action={
        <span className="flex items-center gap-0.5">
          <AlertDialog>
            {/* wp/ACTIONS: Rename and Delete as visible hover icons; the … menu stays. */}
            <span className={cn("flex items-center gap-0.5", CHECKLIST_HOVER)}>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Rename ${list.title}`}
                title="Rename"
                className="text-muted-foreground"
                onClick={() => setRenaming(true)}
              >
                <PencilIcon aria-hidden="true" />
              </Button>
              <AlertDialogTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Delete ${list.title}`}
                    title="Delete"
                    className="text-muted-foreground hover:text-destructive"
                  />
                }
              >
                <Trash2Icon aria-hidden="true" />
              </AlertDialogTrigger>
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`${list.title} actions`}
                    className="text-muted-foreground"
                  />
                }
              >
                <EllipsisIcon aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-48"
                finalFocus={() => titleInput.current ?? true}
              >
                <DropdownMenuItem onClick={() => setRenaming(true)}>
                  <PencilIcon aria-hidden="true" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuCheckboxItem
                  checked={hidden}
                  onCheckedChange={(on) => {
                    setHidden(Boolean(on));
                    writeHidden(list.id, Boolean(on));
                  }}
                >
                  Hide completed
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <AlertDialogTrigger render={<DropdownMenuItem variant="destructive" />}>
                  <Trash2Icon aria-hidden="true" />
                  Delete checklist
                </AlertDialogTrigger>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete “{list.title}”?</AlertDialogTitle>
                <AlertDialogDescription>
                  {items.length
                    ? `Its ${items.length} ${items.length === 1 ? "item goes" : "items go"} with it.`
                    : "It has no items."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction variant="destructive" onClick={() => w.removeList(list.id)}>
                  Delete checklist
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </span>
      }
    >
      {items.length ? (
        <Progress
          value={Math.round((done / items.length) * 100)}
          aria-label={`${list.title}: ${done} of ${items.length} done`}
          className="mb-1 **:data-[slot=progress-track]:h-1"
        />
      ) : null}
      <ul
        ref={listRef}
        aria-label={list.title}
        className={cn(
          "m-0 flex min-h-1 list-none flex-col rounded-md p-0",
          drag && target?.list === list.id && "bg-muted/60",
        )}
        onDragOver={onDragOver}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onTarget(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDrop(visible);
        }}
      >
        {visible.map((item, i) => (
          <ChecklistRow
            key={item.id}
            item={item}
            people={people}
            writes={w}
            today={today}
            dropBefore={drag !== null && target?.list === list.id && target.index === i}
            dragging={drag?.id === item.id}
            onDragStart={() => onDragStart(item.id)}
            onDragEnd={onDragEnd}
            onKeyDown={(e) => onRowKeyDown(e, item.id)}
          />
        ))}
        {drag && target?.list === list.id && target.index === visible.length ? <DropLine /> : null}
      </ul>
      {hidden && done ? (
        <p className="mt-1 text-xs text-muted-foreground">{done} completed hidden</p>
      ) : null}
      <Input
        aria-label={`Add an item to ${list.title}`}
        placeholder="Add an item — Enter to add"
        className="mt-2 h-8"
        maxLength={500}
        autoFocus={autoFocusAdd}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addItem();
          }
        }}
      />
    </DrawerSection>
  );
}

function DropLine() {
  return <li aria-hidden={true} className="h-0.5 list-none rounded-full bg-primary" />;
}

function ChecklistRow({
  item,
  people,
  writes: w,
  today,
  dropBefore,
  dragging,
  onDragStart,
  onDragEnd,
  onKeyDown,
}: {
  item: Item;
  people: SeatPeople;
  writes: Writes;
  today: string;
  dropBefore: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLLIElement>) => void;
}) {
  const [editing, setEditing] = useState(false);
  const done = item.done_at !== null;
  return (
    <>
      {dropBefore ? <DropLine /> : null}
      <li
        data-checklist-item={item.id}
        data-done={done || undefined}
        draggable={!editing}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", item.id);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        onKeyDown={onKeyDown}
        className={cn(
          "group/item flex h-8 items-center gap-2 border-b border-subtle",
          dragging && "opacity-50",
        )}
      >
        <GripVerticalIcon
          aria-hidden="true"
          data-drag-handle=""
          className={cn("size-3.5 shrink-0 cursor-grab text-faint", ON_HOVER)}
        />
        <Checkbox
          checked={done}
          aria-label={item.text}
          onCheckedChange={(v) => w.update(item.id, { done: Boolean(v) })}
        />
        <InlineText
          value={item.text}
          editing={editing}
          onEditingChange={setEditing}
          buttonLabel={`Edit ${item.text}`}
          inputLabel="Item text"
          maxLength={500}
          buttonClassName={cn(
            "min-w-0 flex-1 truncate rounded-sm text-left text-sm",
            done && "text-muted-foreground line-through",
          )}
          inputClassName="h-7 flex-1 px-2"
          onSave={(v) => w.update(item.id, { text: v })}
        />
        {editing ? null : (
          <>
            <AssigneeMenu
              compact
              withDelegates={false}
              people={people}
              seatIds={item.assignee_seat_id ? [item.assignee_seat_id] : []}
              title={item.text}
              onToggle={(seat, on) => w.update(item.id, { assignee_seat_id: on ? seat : null })}
              className="shrink-0 group-hover/item:opacity-100 group-focus-within/item:opacity-100 data-popup-open:opacity-100"
            />
            <DueControl
              item={item}
              today={today}
              onSave={(due) => w.update(item.id, { due_at: due })}
            />
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Delete ${item.text}`}
              className={cn("shrink-0 text-muted-foreground", ON_HOVER)}
              onClick={() => w.remove(item.id)}
            >
              <XIcon aria-hidden="true" />
            </Button>
          </>
        )}
      </li>
    </>
  );
}

/** Text that turns into a field on click (or Enter): Enter and blur save, Esc cancels. */
function InlineText({
  value,
  editing,
  onEditingChange,
  onSave,
  inputLabel,
  buttonLabel,
  maxLength,
  inputRef,
  buttonClassName,
  inputClassName,
}: {
  value: string;
  editing: boolean;
  onEditingChange: (on: boolean) => void;
  onSave: (v: string) => void;
  inputLabel: string;
  buttonLabel?: string;
  maxLength: number;
  inputRef?: RefObject<HTMLInputElement | null>;
  buttonClassName?: string;
  inputClassName?: string;
}) {
  const [draft, setDraft] = useState(value);
  const cancelled = useRef(false);
  const button = useRef<HTMLButtonElement>(null);
  const back = useRef(false);
  // Opening (by a click or the menu's Rename) starts from the current text; closing by key
  // puts the focus back on the text.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on opening and closing.
  useEffect(() => {
    if (editing) {
      setDraft(value);
      cancelled.current = false;
    } else if (back.current) {
      back.current = false;
      button.current?.focus();
    }
  }, [editing]);
  const finish = (save: boolean) => {
    if (cancelled.current) return;
    cancelled.current = true;
    const v = draft.trim();
    if (save && v && v !== value) onSave(v);
    onEditingChange(false);
  };
  if (!editing)
    return (
      <button
        ref={button}
        type="button"
        data-item-text=""
        aria-label={buttonLabel}
        className={cn(
          "outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring focus-visible:outline-solid",
          buttonClassName,
        )}
        onClick={() => onEditingChange(true)}
      >
        {value}
      </button>
    );
  return (
    <Input
      ref={inputRef}
      aria-label={inputLabel}
      autoFocus
      maxLength={maxLength}
      className={inputClassName}
      value={draft}
      onFocus={() => {
        cancelled.current = false;
      }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          back.current = true;
          finish(true);
        } else if (e.key === "Escape") {
          e.stopPropagation();
          back.current = true;
          finish(false);
        }
      }}
      onBlur={() => finish(true)}
    />
  );
}

/** One due date: a chip when set (red when past and not done), a calendar on hover when not. */
function DueControl({
  item,
  today,
  onSave,
}: {
  item: Item;
  today: string;
  onSave: (due: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [due, setDue] = useState("");
  const id = useId();
  const day = item.due_at ? localDay(item.due_at) : null;
  const trigger: ReactElement = day ? (
    <button
      type="button"
      aria-label={`Due ${day}: change the due date of ${item.text}`}
      className="inline-flex h-6 shrink-0 items-center rounded-md px-1 text-xs text-muted-foreground hover:bg-muted"
    />
  ) : (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={`Set a due date for ${item.text}`}
      className={cn("shrink-0 text-muted-foreground", ON_HOVER)}
    />
  );
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setDue(day ?? "");
      }}
    >
      <PopoverTrigger render={trigger}>
        {day ? (
          <DueLabel due={day} overdue={day < today && item.done_at === null} />
        ) : (
          <CalendarIcon aria-hidden="true" />
        )}
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSave(due ? dueInstant(due) : null);
            setOpen(false);
          }}
        >
          <Field>
            <FieldLabel htmlFor={id}>Due</FieldLabel>
            <Input id={id} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setDue("")}>
              Clear
            </Button>
            <Button type="submit" size="sm">
              Save
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
